// React import
import { useState, useEffect, useRef, useMemo } from 'react';

// Generic imports
import { useDrop } from 'react-dnd';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

// Mui imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { grey } from '@mui/material/colors';

// Local imports
import { findMatchingProducts, normalizeProductType } from '../utils/product.js';
import { convertRateKgToLbsIfNeeded } from '../utils/rates.js';
import { templateFieldFilter } from './templateFields.js';

// Walk an offers tree, build a (title → field-id) lookup, and rewrite every
// formula string in field.values so that "<FieldName>" → "field_<uuid_with_underscores>"
// of a sibling field with that title. Lets templates carry the friendly
// shorthand the formula editor exposes and have it resolve at apply-time
// against the freshly-uuid'd opportunity fields.
function resolveFieldNamePlaceholdersInOffers(offers) {
    const byName = {};
    (offers || []).forEach(o => (o.sections || []).forEach(sec => (sec.offers || []).forEach(so => (so.fields || []).forEach(f => {
        if (f?.id && f.title && !byName[f.title.toLowerCase()]) byName[f.title.toLowerCase()] = f.id;
    }))));
    if (_.isEmpty(byName)) return;
    (offers || []).forEach(o => (o.sections || []).forEach(sec => (sec.offers || []).forEach(so => (so.fields || []).forEach(f => {
        _.forEach(f.values, (v) => {
            if (typeof v?.formula !== 'string' || !v.formula.includes('<')) return;
            v.formula = v.formula.replace(/<([^<>]+)>/g, (m, name) => {
                const id = byName[name.trim().toLowerCase()];
                return id ? `field_${id.replace(/-/g, '_')}` : m;
            });
        });
    }))));
}

const FEATURE_TOGGLE_CONTRACTS = false;

function AddTemplate(props) {
    const { showAddGroupAnimation, forms, products, routes, transportationMethod, offers, setOffers, addOffer, 
        setBuyingRates, disabled, type = 'opportunity', noDragDrop = false, noScratch = false, opportunities } = props;
    const [ templateSelection, setTemplateSelection ] = useState(null);
    const [ dropOver, setDropOver ] = useState(null);

    const first = useRef();
    const setDropOverRef = useRef();
    const createOfferFromRate = useRef();
    setDropOverRef.current = setDropOver;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop');
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            createOfferFromRate.current(item.rate);
        },
        canDrop: (item, monitor) => {
            return true;
        },
    }));

    createOfferFromRate.current = (rate) => {
        // Convert rate from KG to LBS if needed before using its values
        const convertedRate = convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products });
        const form = convertedRate.offer;
        const route = { source: convertedRate.source, destination: convertedRate.destination };

        const convertMap = {};

        const matchingProducts = findMatchingProducts({ product: convertedRate.product, products });

        const newOffer = { 
            id: uuidv4(), 
            title: route ? `${route.source?.text || 'Eveywhere'} to ${route.destination?.text || 'Eveywhere'}` : '',
            description: '',
            sections: form.sections.map(s => ({
                id: uuidv4(),
                title: s.title,
                description: s.description,
                offers: [{
                    id: uuidv4(),
                    fields: _.compact(s.offers[0].fields.map(f => {
                        const id = uuidv4();
                        convertMap[f.id] = id;
                        return {
                            id,
                            template: f.id,
                            title: f.title,
                            type: f.type,
                            values: {
                                ..._.fromPairs([...products, {id: 'flat'}, {id: 'custom'}].map(p => ([p.id, {
                                    currency: _.first(_.values(f.values))?.currency || s.defaultCurrency, 
                                    value: convertedRate.type === 'tariff' ? f.values[convertedRate.product.id].value : null,
                                    formula: convertedRate.type === 'tariff' ? f.values[convertedRate.product.id].formula : null,
                                }]))),
                            },
                            buyingRates: convertedRate.type === 'tariff' ? [] : 
                                matchingProducts.map(p => ({
                                    rateId: convertedRate.id,
                                    fieldId: f.id,
                                    opportunityProductId: p.id,
                                })),
                            cargowiseChargeCode: f.cargowiseChargeCode,
                        }
                    })),
                }]
            }))
        };

        newOffer.sections.forEach(section => section.offers.forEach(offer => offer.fields.forEach(field => _.forEach(field.values, (value, key) => {
            if (value.formula) _.forEach(convertMap, (destId, srcId) => value.formula = value.formula.replace(srcId.replace(/-/g, '_'), destId.replace(/-/g, '_')));
        }))));

        if (convertedRate.type !== 'tariff') {
            setBuyingRates(buyingRates => _.uniqBy([...(buyingRates || []), convertedRate], br => br.id));
        }

        if (addOffer) {
            addOffer({ rate: convertedRate, offers: [newOffer], preface: form.preface, convertMap });
        } else {
            setOffers([...offers, newOffer]);
        }
    }

    function createOfferFromScratch() {
        const index = offers.length;
        const route = !routes || routes?.length === 0 ? null : index < routes.length ? routes[index] : null;
        const title = route ? `${route.source?.text || 'Everywhere'} to ${route.destination?.text || 'Everywhere'}` : '';
        if (addOffer) {
            addOffer({ offers: [{ id: uuidv4(), title, sections: [] }] });
        } else {
            setOffers([...offers, { id: uuidv4(), title, sections: [] }]);
        }
    }

    function createOfferFromForm(formId) {
        const form = forms[formId];
        if (!form) return createOfferFromOpportunity(formId);
        const index = offers.length;
        const route = routes.length === 0 ? null : index < routes.length ? routes[index] : null;

        const convertMap = {};

        // Template fields that share an id: identical copies are dropped, different charges kept
        // (see templateFieldFilter). Tracked across all sections.
        const keepTemplateField = templateFieldFilter();

        // Manual buying rates synthesized from each field's template `defaultCost`.
        // Collected here and pushed into the opportunity's buyingRates after the
        // offer is built (mirrors how a dragged rate card registers its rate).
        const newBuyingRates = [];

        const newOffer = {
            id: uuidv4(),
            template: formId,
            title: route ? `${route.source?.text || 'Eveywhere'} to ${route.destination?.text || 'Eveywhere'}` : '',
            description: '',
            ...(form.displayCurrency && form.displayCurrencyEnabled !== false ? { displayCurrency: form.displayCurrency, displayCurrencySource: form.displayCurrencySource } : {}),
            sections: form.sections.map(s => ({
                id: uuidv4(),
                title: s.title,
                description: s.description,
                ...(s.displayCurrency && form.displayCurrencyEnabled !== false ? { displayCurrency: s.displayCurrency, displayCurrencySource: s.displayCurrencySource } : {}),
                offers: [{
                    id: uuidv4(),
                    fields: s.fields.filter(keepTemplateField).map(f => {
                        const id = uuidv4();
                        // A formula naming a shared id means the first field with it.
                        if (!convertMap[f.id]) convertMap[f.id] = id;
                        const fieldObj = {
                            id,
                            template: f.id,
                            title: f.title,
                            type: f.type,
                            ...(f.displayCurrency && form.displayCurrencyEnabled !== false ? { displayCurrency: f.displayCurrency, displayCurrencySource: f.displayCurrencySource } : {}),
                            values: f.type === 'string' ? {
                                string: { value: f.default?.value || '' },
                            } : {
                                ..._.fromPairs([...products, {id: 'flat'}, {id: 'custom'}].map(p => ([p.id, { currency: f.currency || s.defaultCurrency, ...(f.default || {}) }]))),
                            },
                            cargowiseChargeCode: f.cargowiseChargeCode,
                        };

                        // Default cost → manual buying rate(s) (platform -2). One rate per product
                        // type so each rate's product.type matches the products it is attached to
                        // (keeps the cost tied to its type; see the type guard in getFieldRatesForProduct).
                        const dc = f.defaultCost;
                        if (dc && dc.value != null && dc.value !== '' && dc.supplier?.organization && f.type !== 'string' && f.type !== 'date') {
                            const targets = dc.product
                                ? (products || []).filter(p => normalizeProductType(p.type) === normalizeProductType(dc.product))
                                : (products || []);
                            if (targets.length > 0) {
                                const currency = dc.currency || f.currency || s.defaultCurrency;
                                const costValue = { value: dc.value, currency, ...(dc.formula ? { formula: dc.formula } : {}) };
                                const supplier = {
                                    organization: dc.supplier.organization,
                                    ...(dc.supplier.uniqueId ? { uniqueId: dc.supplier.uniqueId } : {}),
                                    ...(dc.supplier.email ? { email: dc.supplier.email } : {}),
                                };
                                const allRefs = [];
                                _.forEach(_.groupBy(targets, p => normalizeProductType(p.type)), groupProducts => {
                                    const rateId = uuidv4();
                                    const costFieldId = uuidv4();
                                    const rateProduct = _.cloneDeep(groupProducts[0]);
                                    // Key the cost under every product id plus flat/custom (mirroring the
                                    // sell field), so the renderer finds it whichever key it looks up
                                    // (e.g. `flat`-type fields read values.flat).
                                    const costValues = _.fromPairs([...(products || []), { id: 'flat' }, { id: 'custom' }].map(p => [p.id, { ...costValue }]));
                                    newBuyingRates.push({
                                        id: rateId,
                                        platform: -2,
                                        transportationMethod,
                                        supplier,
                                        product: rateProduct,
                                        offer: {
                                            id: uuidv4(),
                                            sections: [{
                                                id: uuidv4(),
                                                offers: [{
                                                    id: uuidv4(),
                                                    fields: [{
                                                        id: costFieldId,
                                                        title: f.title,
                                                        type: dc.calculationMethod || f.type,
                                                        values: costValues,
                                                    }],
                                                }],
                                            }],
                                        },
                                    });
                                    groupProducts.forEach(p => allRefs.push({ rateId, fieldId: costFieldId, opportunityProductId: p.id }));
                                });
                                fieldObj.buyingRates = allRefs;
                            }
                        }

                        return fieldObj;
                    }),
                }]
            }))
        };

        newOffer.sections.forEach(section => section.offers.forEach(offer => offer.fields.forEach(field => _.forEach(field.values, (value, key) => {
            if (value.formula) _.forEach(convertMap, (destId, srcId) => value.formula = value.formula.replace(srcId.replace(/-/g, '_'), destId.replace(/-/g, '_')));
        }))));

        // Resolve "<FieldName>" placeholders that survived in template formulas
        // (e.g. authored before the editor's name→id rewrite). Maps to the freshly-
        // generated opportunity field uuids on this offer.
        resolveFieldNamePlaceholdersInOffers([newOffer]);

        if (setBuyingRates && newBuyingRates.length > 0) {
            setBuyingRates(buyingRates => _.uniqBy([...(buyingRates || []), ...newBuyingRates], br => br.id));
        }

        if (addOffer) {
            addOffer({ offers: [newOffer], preface: form.preface, convertMap });
        } else {
            setOffers([...offers, newOffer]);
        }
    }

    function createOfferFromOpportunity(opportunityId) {
        const fieldTransform = {};
        const _opportunity = _.cloneDeep(opportunities.find(o => o.id === opportunityId));
        const _offers = _.cloneDeep(_opportunity.offers);

        _offers.forEach(offer => {
            offer.id = uuidv4();
            offer.sections.forEach(section => {
                section.id = uuidv4();
                section.offers.forEach(sectionOffer => {
                    sectionOffer.id = uuidv4();
                    sectionOffer.fields.forEach(field => {
                        const newId = uuidv4();
                        fieldTransform[field.id] = newId;
                        field.id = newId;

                        _.forEach(products, p => {
                            const matchingProducts = findMatchingProducts({ product: p, products: _opportunity.products });
                            const matchedId = matchingProducts.length > 0 ? matchingProducts[0].id : _opportunity.products[0].id;
                            field.values[p.id] = field.values[matchedId];
                        })

                        field.values = _.omitBy(field.values, (v, k) => _opportunity.products.find(p => p.id === k));
                    })
                })
            })
        })

        let _preface = _opportunity.preface || '';
        _.forEach(fieldTransform, (newId, oldId) => {
            _preface = _preface.replaceAll(oldId, newId);
        })

        // Resolve "<FieldName>" placeholders that may exist in formulas on the
        // source opportunity (e.g. authored via the editor's name shorthand
        // before save). Maps to the freshly-uuid'd opportunity field ids.
        resolveFieldNamePlaceholdersInOffers(_offers);

        if (addOffer) {
            addOffer({ offers: _offers, preface: _preface });
        } else {
            setOffers([...offers, ..._offers]);
        }
    }

    const _forms = _.pickBy(forms, f => !f.deleted &&
        f.type === type &&
        (!f.transportationMethods || f.transportationMethods.includes(transportationMethod)));
    
    return (
        <Box ref={noDragDrop ? null : drop} sx={{
            width: '100%',
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            mt: 4,
            ...(showAddGroupAnimation ? {
                animation: `zoom-in-zoom-out 1200ms 1 500ms`,
                '@keyframes zoom-in-zoom-out': {
                    '0%': {
                        transform: 'scale(1)', 

                    },
                    '30%': {
                        transform: 'scale(1.05)', 

                    },
                    '20%, 40%': {
                        transform: 'scale(1.1)', 
                    },
                    '70%': {
                        transform: 'scale(1)',
                    }
                }
            } : {})
        }}>
        <FormControl sx={{ width: '250px', mr: 2 }}>
                <InputLabel id='pick-template-label'>Load a template</InputLabel>
                <Select
                    labelId='pick-template-label'
                    id='pick-template'
                    label='Load a template'
                    sx={{ flex: 1 }}
                    value={templateSelection || ''}
                    onChange={e => setTemplateSelection(e.target.value)}
                    disabled={disabled}
                >
                    {_.map(_forms, (v,k) => <MenuItem key={k} value={k}>{v.title}</MenuItem>)}

                    {!_.isEmpty(opportunities) && (
                        <>
                            {!_.isEmpty(_forms) && <Divider />} 
                            <Typography variant='caption' component={Box} sx={{ paddingLeft: '16px', opacity: 0.5 }}>Opportunities</Typography>
                        </>
                    )}
                    {!_.isEmpty(opportunities) && opportunities.map(o => (
                        <MenuItem key={o.id} value={o.id}>{o.title}</MenuItem>
                    ))}

                    {!noScratch && (_.isEmpty(_forms) || _.isEmpty(opportunities)) && <Divider />}
                    {!noScratch && (<MenuItem value='scratch'><i>Start from empty offer</i></MenuItem>)}
                </Select>
            </FormControl>
            <Button
                disabled={!templateSelection || disabled}  
                variant='contained' 
                sx={{ p: 2 }} 
                onClick={() => templateSelection === 'scratch' ? createOfferFromScratch() : createOfferFromForm(templateSelection)}
            >
                + Offer
            </Button>
            {!noDragDrop && (
                <>
                    <Typography variant='caption' component={Box} sx={{ paddingLeft: '24px' }}>
                        or drag and drop a rate card to start
                    </Typography>
                    {dropOver === 'candrop' && (
                        <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'start' }}>
                            Drop your rate here
                        </Box>
                    )}
                    {dropOver === 'candrop-tariff' && (
                        <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'start' }}>
                            Drop your tariff here
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}

// Materialize a template's default costs for products that now match but aren't yet linked.
// Runs when products are added / type-changed after the template was applied (default costs are
// otherwise only created at template-load for products present then). Idempotent: a default cost
// already linked to a product is skipped, so a stable state produces no change (no render loop).
// Only touches template default costs (platform:-2); never manual/RFQ costs or user-edited ones.
// With `previousProducts` (the products before this change), only products that are new or changed
// type get a default cost: a product already on the quote whose cost the user moved or removed in
// the Rate Settings stays as the user left it when, say, only its quantity changes. Without it,
// every matching product is considered.
export function reconcileTemplateDefaultCosts({ opportunity, forms, previousProducts }) {
    const offers = opportunity?.offers || [];
    const allProducts = opportunity?.products || [];
    const before = previousProducts ? _.keyBy(previousProducts, 'id') : null;
    const products = before
        ? allProducts.filter(p => !before[p.id] || normalizeProductType(before[p.id].type) !== normalizeProductType(p.type))
        : allProducts;
    const transportationMethod = opportunity?.transportationMethod;
    if (!offers.length || !forms || !products.length) return { changed: false };

    const buyingRates = [...(opportunity.buyingRates || [])];
    const isDefaultCostRate = (br, org, type) => br && (br.platform === -2 || br.platform == null)
        && br.supplier?.organization === org
        && normalizeProductType(br.product?.type) === normalizeProductType(type);
    let changed = false;

    const newOffers = offers.map(offer => {
        const form = offer.template && forms[offer.template];
        if (!form) return offer;
        // A field is looked up in the template section it came from (same position and title)
        // first: older templates can share a field id between sections. Otherwise the first
        // template field with that id.
        const ffMap = {};
        (form.sections || []).forEach(s => (s.fields || []).forEach(ff => { if (!ffMap[ff.id]) ffMap[ff.id] = { ff, section: s }; }));
        const lookup = (sectionIndex, section, templateId) => {
            const formSection = (form.sections || [])[sectionIndex];
            const own = formSection && formSection.title === section.title && (formSection.fields || []).find(ff => ff.id === templateId);
            return own ? { ff: own, section: formSection } : ffMap[templateId];
        };

        let offerChanged = false;
        const clonedOffer = _.cloneDeep(offer);
        (clonedOffer.sections || []).forEach((section, sectionIndex) => (section.offers || []).forEach(so => (so.fields || []).forEach(field => {
            const entry = field.template && lookup(sectionIndex, section, field.template);
            if (!entry) return;
            const { ff, section: formSection } = entry;
            const dc = ff.defaultCost;
            if (!dc || dc.value == null || dc.value === '' || !dc.supplier?.organization || field.type === 'string' || field.type === 'date') return;

            const matching = dc.product
                ? products.filter(p => normalizeProductType(p.type) === normalizeProductType(dc.product))
                : products;
            if (!matching.length) return;

            _.forEach(_.groupBy(matching, p => normalizeProductType(p.type)), groupProducts => {
                const refs = field.buyingRates || [];
                const linked = p => refs.some(r => r.opportunityProductId === p.id
                    && isDefaultCostRate(buyingRates.find(br => br.id === r.rateId), dc.supplier.organization, p.type));
                const missing = groupProducts.filter(p => !linked(p));
                if (!missing.length) return;

                // Reuse an existing default-cost rate already on this field for this supplier+type
                // (so adding a second product of the same type doesn't create a duplicate rate).
                const existingRef = refs.find(r => isDefaultCostRate(buyingRates.find(br => br.id === r.rateId), dc.supplier.organization, groupProducts[0].type));
                let rateId, costFieldId;
                if (existingRef) {
                    rateId = existingRef.rateId;
                    costFieldId = existingRef.fieldId;
                } else {
                    rateId = uuidv4();
                    costFieldId = uuidv4();
                    const currency = dc.currency || ff.currency || formSection?.defaultCurrency;
                    const costValue = { value: dc.value, currency, ...(dc.formula ? { formula: dc.formula } : {}) };
                    const supplier = {
                        organization: dc.supplier.organization,
                        ...(dc.supplier.uniqueId ? { uniqueId: dc.supplier.uniqueId } : {}),
                        ...(dc.supplier.email ? { email: dc.supplier.email } : {}),
                    };
                    const costValues = _.fromPairs([...allProducts, { id: 'flat' }, { id: 'custom' }].map(p => [p.id, { ...costValue }]));
                    buyingRates.push({
                        id: rateId,
                        platform: -2,
                        transportationMethod,
                        supplier,
                        product: _.cloneDeep(groupProducts[0]),
                        offer: {
                            id: uuidv4(),
                            sections: [{ id: uuidv4(), offers: [{ id: uuidv4(), fields: [{
                                id: costFieldId,
                                title: field.title,
                                type: dc.calculationMethod || ff.type,
                                values: costValues,
                            }] }] }],
                        },
                    });
                }
                field.buyingRates = [...(field.buyingRates || []), ...missing.map(p => ({ rateId, fieldId: costFieldId, opportunityProductId: p.id }))];
                offerChanged = true;
                changed = true;
            });
        })));
        return offerChanged ? clonedOffer : offer;
    });

    if (!changed) return { changed: false };
    return { changed: true, offers: newOffers, buyingRates };
}

export default AddTemplate;
